/**
 * @module immersive/StagesApp
 * @description Tier 3 shell: Send Love Live. Reuses GivingContext (cart =
 * Quiver), the checkout → simulated Neon hand-off → thank-you pages, tokens
 * and cause data; adds the stage palette, lobby, stages and celebrations.
 */
import type { JSX } from "react";
import { GivingProvider, useGiving } from "../app/GivingContext";
import { navigate, useRoute, type Route } from "../app/router";
import { Toast } from "../components/Cart";
import { Icon } from "../components/Icons";
import { DemoNotice, SiteFooter } from "../components/SiteChrome";
import { CheckoutPage } from "../pages/CheckoutPage";
import { PaymentPage } from "../pages/PaymentPage";
import { ThankYouPage } from "../pages/ThankYouPage";
import { formatUsd } from "../lib/format";
import { Celebration } from "./Celebration";
import { Lobby } from "./Lobby";
import { QuiverDrawer } from "./QuiverDrawer";
import { StagePage } from "./StagePage";
import { StageProvider } from "./StageContext";
import "./Stages.css";

/** Stage header: logo (inverse) + Quiver button with live total. */
function StageHeader(): JSX.Element {
    const { lines, totalUsd, setCartOpen } = useGiving();
    return (
        <header className="stage-header">
            <div className="container stage-header__inner">
                <a href="#/" className="stage-header__logo" onClick={(e) => { e.preventDefault(); navigate("browse"); }}>
                    <img src="brand/afj-horizontal.png" alt="The American Friends of Jamaica" width={184} height={34} />
                    <span className="stage-header__show">Send Love Live</span>
                </a>
                <button type="button" className="quiver-btn" onClick={() => setCartOpen(true)} aria-label={`Open your Quiver, ${formatUsd(totalUsd)}`}>
                    <Icon name="heart-filled" size={18} />
                    <span>Quiver</span>
                    {lines.length > 0 && <strong className="quiver-btn__total" key={totalUsd}>{formatUsd(totalUsd)}</strong>}
                </button>
            </div>
        </header>
    );
}

/** Route → page. Checkout/payment/thank-you are the shared (light) trust pages. */
function StagesPage({ route }: { route: Route }): JSX.Element {
    switch (route) {
        case "stage": return <StagePage />;
        case "checkout": return <CheckoutPage />;
        case "payment": return <PaymentPage />;
        case "thank-you": return <ThankYouPage />;
        case "browse": return <Lobby />;
    }
}

/** Tier 3 root. */
export function StagesApp(): JSX.Element {
    const route: Route = useRoute();
    const onStagePalette: boolean = route === "browse" || route === "stage";
    return (
        <GivingProvider>
            <StageProvider>
                <div className={`app app--motion${onStagePalette ? " app--stage" : ""}`}>
                    <DemoNotice />
                    <StageHeader />
                    <div className={`route route--${route}`} key={`${route}${window.location.hash}`}>
                        <StagesPage route={route} />
                    </div>
                    <SiteFooter />
                    <QuiverDrawer />
                    <Celebration />
                    <Toast />
                </div>
            </StageProvider>
        </GivingProvider>
    );
}
